import { useState, useEffect, useMemo } from 'react';
import { Activity, Loader2, RefreshCw, AlertTriangle, Clock, MousePointerClick, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../api/client';
import FeatureUsageHeatmap from '../components/FeatureUsageHeatmap';
import ActivityTables, { PlayerGameTimeCard } from '../components/ActivityTables';

const RANGE_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

// Upper bound for both date pickers — there's no activity from the future.
const todayIso = new Date().toISOString().slice(0, 10);

function formatDuration(ms) {
  if (!ms) return '0m';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Admin-dashboard USAGE analytics (which admin pages/features get used, how
// much time is spent on each, when in the day) — fed by the new
// admin_activity_events table, distinct from the security-focused Audit Logs
// page. See backend app.models.AdminActivityEvent's docstring for why the two
// are kept separate.
export default function AdminActivity() {
  const [range, setRange] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  // PC actions (GET /activity/pc-actions) load on their own, so a backend
  // that doesn't have the endpoint yet only blanks this one card.
  const [pcActions, setPcActions] = useState(null);
  const [pcActionsError, setPcActionsError] = useState(null);
  const [openAction, setOpenAction] = useState(null);

  const fetchPcActions = async (params) => {
    setPcActionsError(null);
    try {
      const res = await api.get('/activity/pc-actions', { params });
      setPcActions(res.data || null);
    } catch (err) {
      console.error('Failed to fetch PC actions:', err);
      setPcActions(null);
      setPcActionsError(
        err.response?.status === 404
          ? 'Not available on the server yet — the backend needs to be updated.'
          : err.response?.data?.detail || 'Failed to load PC actions'
      );
    }
  };

  const fetchSummary = async () => {
    if (range === 'custom' && (!customStart || !customEnd)) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = range === 'custom'
        ? {
          start: `${customStart}T00:00:00`,
          // end is EXCLUSIVE server-side, so push it to the next midnight —
          // picking 1st-20th means "through the end of the 20th", not "up
          // to its start". Same fix Analytics.jsx's café revenue trend uses.
          end: (() => {
            const endNext = new Date(`${customEnd}T00:00:00`);
            endNext.setDate(endNext.getDate() + 1);
            return endNext.toISOString().slice(0, 19);
          })(),
        }
        : { days: RANGE_DAYS[range] };
      fetchPcActions(params);
      const res = await api.get('/activity/summary', { params });
      setSummary(res.data || null);
    } catch (err) {
      console.error('Failed to fetch admin activity summary:', err);
      setError(
        err.response?.status === 404
          ? 'This endpoint is not available on the server yet — the backend needs to be updated.'
          : err.response?.data?.detail || 'Failed to load admin activity data'
      );
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Custom range fetches only on explicit "Apply" (below), not per keystroke
  // — typing into the date inputs must never trigger a re-render that could
  // steal focus mid-entry (that's what made the year hard to type).
  useEffect(() => {
    if (range !== 'custom') fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const applyCustomRange = () => {
    if (customStart && customEnd) fetchSummary();
  };

  const topPagesChart = useMemo(
    () => (summary?.top_pages || []).map((p) => ({ name: p.key, views: p.count })),
    [summary]
  );

  // Heatmap grid: rows are the pages that actually have events, columns are
  // hours 0-23. Built client-side from the flat (page, hour, count) list the
  // backend returns, so adding/removing pages needs no frontend change.
  const heatmap = useMemo(() => {
    const cells = summary?.heatmap || [];
    const pages = [...new Set(cells.map((c) => c.page))];
    const byPage = Object.fromEntries(pages.map((p) => [p, new Array(24).fill(0)]));
    let max = 1;
    for (const c of cells) {
      byPage[c.page][c.hour] = c.count;
      if (c.count > max) max = c.count;
    }
    // Busiest page first — most useful row to see without scrolling.
    pages.sort((a, b) => byPage[b].reduce((s, v) => s + v, 0) - byPage[a].reduce((s, v) => s + v, 0));
    return { pages, byPage, max };
  }, [summary]);

  const heatColor = (count, max) => {
    if (!count) return 'rgba(255,255,255,0.03)';
    const t = Math.min(1, count / max);
    return `rgba(254, 123, 0, ${0.12 + t * 0.7})`;
  };

  return (
    <div className="admin-activity">
      <div className="page-header">
        <div>
          <h1 className="page-title">Activity tracker</h1>
          <p className="page-subtitle">Which admin dashboard pages and features are actually used</p>
        </div>
        <div className="header-actions">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="input select time-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="custom">Custom range</option>
          </select>
          {range === 'custom' && (
            <div className="range-picker">
              <input
                type="date"
                value={customStart}
                max={customEnd || todayIso}
                onChange={(e) => setCustomStart(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyCustomRange()}
                aria-label="Start date"
                className="input range-picker__input"
              />
              <span className="range-picker__sep">–</span>
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                max={todayIso}
                onChange={(e) => setCustomEnd(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyCustomRange()}
                aria-label="End date"
                className="input range-picker__input"
              />
              <button
                type="button"
                className="btn btn--secondary range-picker__apply"
                onClick={applyCustomRange}
                disabled={!customStart || !customEnd || isLoading}
              >
                Apply
              </button>
            </div>
          )}
          <button className="btn btn--secondary" onClick={fetchSummary} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-activity-error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="admin-activity-loading"><Loader2 size={28} className="spin" /></div>
      ) : !summary ? null : (
        <>
          <div className="metrics-grid">
            <div className="metric-card glass-card">
              <span className="metric-card__label">Total events</span>
              <span className="metric-card__value">{summary.total_events}</span>
            </div>
            <div className="metric-card glass-card">
              <span className="metric-card__label">Pages tracked</span>
              <span className="metric-card__value">{heatmap.pages.length}</span>
            </div>
            <div className="metric-card glass-card">
              <span className="metric-card__label">Most-used page</span>
              <span className="metric-card__value metric-card__value--sm">
                {summary.top_pages[0]?.key || '—'}
              </span>
            </div>
            <div className="metric-card glass-card">
              <span className="metric-card__label">Least-used page</span>
              <span className="metric-card__value metric-card__value--sm">
                {summary.least_pages[0]?.key || '—'}
              </span>
            </div>
          </div>

          <div className="chart-card glass-card">
            <h3 className="chart-card__title">Most-visited pages</h3>
            {topPagesChart.length === 0 ? (
              <div className="coming-soon"><Clock size={20} /><span>No activity recorded yet for this range.</span></div>
            ) : (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={topPagesChart} layout="vertical" margin={{ left: 24 }}>
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} width={140} tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }} />
                    <Tooltip contentStyle={{ background: 'rgba(26,26,36,0.95)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: 'white' }} />
                    <Bar dataKey="views" fill="#FE7B00" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bottom-grid">
            <div className="glass-card">
              <h3 className="card-title">Most time spent</h3>
              {summary.top_pages.length === 0 ? (
                <div className="coming-soon"><Clock size={20} /><span>No data yet.</span></div>
              ) : (
                <div className="usage-list">
                  {[...summary.top_pages]
                    .sort((a, b) => b.total_duration_ms - a.total_duration_ms)
                    .slice(0, 8)
                    .map((p) => (
                      <div key={p.key} className="usage-row">
                        <span className="usage-row__name">{p.key}</span>
                        <span className="usage-row__value">{formatDuration(p.total_duration_ms)}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="glass-card">
              <h3 className="card-title">Least-visited pages</h3>
              {summary.least_pages.length === 0 ? (
                <div className="coming-soon"><Clock size={20} /><span>No data yet.</span></div>
              ) : (
                <div className="usage-list">
                  {summary.least_pages.map((p) => (
                    <div key={p.key} className="usage-row">
                      <span className="usage-row__name">{p.key}</span>
                      <span className="usage-row__value">{p.count} view{p.count === 1 ? '' : 's'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="glass-card heatmap-card">
            <h3 className="card-title">
              <Activity size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
              Usage heatmap — page × hour of day
            </h3>
            {heatmap.pages.length === 0 ? (
              <div className="coming-soon"><Clock size={20} /><span>No data yet for this range.</span></div>
            ) : (
              <div className="heatmap-scroll">
                <table className="heatmap-table">
                  <thead>
                    <tr>
                      <th className="heatmap-table__page">Page</th>
                      {Array.from({ length: 24 }, (_, h) => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmap.pages.map((page) => (
                      <tr key={page}>
                        <td className="heatmap-table__page">{page}</td>
                        {heatmap.byPage[page].map((count, h) => (
                          <td key={h} style={{ background: heatColor(count, heatmap.max) }} title={`${page} · ${h}:00 · ${count} event${count === 1 ? '' : 's'}`}>
                            {count > 0 ? count : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!isLoading && (pcActions || pcActionsError) && (
        <div className="glass-card pc-actions-card">
          <h3 className="card-title">
            <MousePointerClick size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
            PC actions — café admins &amp; staff
          </h3>
          {pcActionsError ? (
            <div className="coming-soon"><AlertTriangle size={20} /><span>{pcActionsError}</span></div>
          ) : pcActions.actions.length === 0 ? (
            <div className="coming-soon"><Clock size={20} /><span>No PC actions recorded for this range.</span></div>
          ) : (
            <>
              <p className="pc-actions-note">
                {pcActions.total.toLocaleString('en-IN')} actions. The NoLag team&apos;s own actions are left out.
                Taps on the same PC within 10 seconds count once
                {pcActions.repeats_dropped > 0 && ` (${pcActions.repeats_dropped.toLocaleString('en-IN')} repeat taps not counted)`}.
                Click an action to see which cafés use it.
              </p>
              <div className="pc-actions-list">
                {pcActions.actions.map((a) => {
                  const max = pcActions.actions[0].count || 1;
                  const partial = a.first_recorded && new Date(a.first_recorded) > new Date(pcActions.range_start);
                  const isOpen = openAction === a.key;
                  return (
                    <div key={a.key} className="pc-action">
                      <button
                        type="button"
                        className="pc-action__row"
                        onClick={() => setOpenAction(isOpen ? null : a.key)}
                        aria-expanded={isOpen}
                      >
                        <span className="pc-action__name">
                          <ChevronRight size={14} className={`pc-action__chev${isOpen ? ' is-open' : ''}`} />
                          {a.label}
                          {partial && (
                            <span
                              className="pc-action__since"
                              title="This action was not recorded before this date, so earlier days in the range show nothing for it."
                            >
                              since {new Date(a.first_recorded).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          )}
                        </span>
                        <span className="pc-action__bar"><span style={{ width: `${(a.count / max) * 100}%` }} /></span>
                        <span className="pc-action__count">{a.count.toLocaleString('en-IN')}</span>
                      </button>
                      {isOpen && (
                        <div className="pc-action__cafes">
                          {a.cafes.map((c) => (
                            <div key={c.cafe_id} className="usage-row">
                              <span className="usage-row__name">{c.cafe_name || `Café #${c.cafe_id}`}</span>
                              <span className="usage-row__value">{c.count.toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                          {a.repeats_dropped > 0 && (
                            <div className="pc-action__repeats">
                              {a.repeats_dropped.toLocaleString('en-IN')} repeat tap{a.repeats_dropped === 1 ? '' : 's'} not counted
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Customer-facing feature usage + activity — moved here from the
          Overview page so admin-usage and customer-usage tracking live on
          one Activity page. */}
      <FeatureUsageHeatmap />
      <ActivityTables />
      <PlayerGameTimeCard />

      <style>{`
        .admin-activity {
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
          max-width: 1300px;
          /* The page heatmap, the feature-usage grid and the tables are wider
             than a narrow screen. Without this the layout's main column (a
             flex item, min-width:auto) grew to fit them and the whole page
             scrolled sideways; now they scroll inside their own boxes. */
          contain: inline-size;
        }

        .admin-activity .glass-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
          padding: var(--space-6);
        }
        .admin-activity .glass-card::before { display: none; }

        .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-8); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }
        .header-actions { display: flex; gap: var(--space-3); align-items: center; flex-wrap: wrap; }
        .time-select { width: 160px; }
        .range-picker { display: inline-flex; align-items: center; gap: 6px; }
        .range-picker__input { font-size: var(--text-xs); width: 132px; padding: 6px 8px; }
        .range-picker__sep { font-size: var(--text-xs); color: var(--text-tertiary); }
        .range-picker__apply { padding: 6px 12px; font-size: var(--text-xs); }

        .admin-activity-error { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); margin-bottom: var(--space-6); background: var(--status-danger-subtle); color: var(--status-danger); border-radius: var(--radius-md); font-size: var(--text-sm); }
        .admin-activity-loading { display: flex; justify-content: center; padding: var(--space-10) 0; color: var(--text-tertiary); }

        .coming-soon { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-6); color: var(--text-tertiary); font-size: var(--text-sm); }
        .coming-soon svg { flex-shrink: 0; }

        .metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-5); margin-bottom: var(--space-8); }
        .metric-card { padding: var(--space-6); }
        .metric-card__label { display: block; font-size: var(--text-base); line-height: 1.4; font-weight: 400; color: var(--text-primary); margin-bottom: var(--space-2); }
        .metric-card__value { display: block; font-size: var(--text-3xl); font-weight: 600; letter-spacing: -0.02em; }
        .metric-card__value--sm { font-size: var(--text-lg); }

        .chart-card { padding: var(--space-6); margin-bottom: var(--space-8); }
        .chart-card__title { font-size: var(--text-lg); font-weight: 600; margin-bottom: var(--space-6); }
        .chart-container { margin: 0 calc(var(--space-3) * -1); }

        .bottom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-6); margin-bottom: var(--space-6); }
        .card-title { font-size: var(--text-lg); font-weight: 600; margin-bottom: var(--space-5); }

        .usage-list { display: flex; flex-direction: column; max-height: 280px; overflow-y: auto; }
        .usage-row { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); padding: var(--space-3) 0; border-bottom: 1px solid var(--divider); }
        .usage-row:last-child { border-bottom: none; }
        .usage-row__name { font-weight: 500; }
        .usage-row__value { font-family: var(--font-mono); color: var(--text-tertiary); font-size: var(--text-sm); }

        .heatmap-card { margin-bottom: var(--space-6); }
        .heatmap-scroll { overflow-x: auto; }
        .heatmap-table { border-collapse: collapse; font-size: var(--text-xs); }
        .heatmap-table th { font-weight: 500; color: var(--text-tertiary); padding: 4px 6px; text-align: center; }
        .heatmap-table td { width: 24px; height: 24px; text-align: center; border: 1px solid rgba(255,255,255,0.05); color: rgba(255,255,255,0.8); }
        .heatmap-table__page { text-align: left !important; padding: 4px 10px 4px 0 !important; white-space: nowrap; font-weight: 500; }

        .pc-actions-card { margin-bottom: var(--space-6); }
        .pc-actions-note { font-size: var(--text-sm); color: var(--text-tertiary); margin: calc(var(--space-2) * -1) 0 var(--space-4); }
        .pc-actions-list { display: flex; flex-direction: column; }
        .pc-action { border-bottom: 1px solid var(--divider); }
        .pc-action:last-child { border-bottom: none; }
        .pc-action__row { display: grid; grid-template-columns: minmax(180px, 1.2fr) 2fr 64px; align-items: center; gap: var(--space-4); width: 100%; padding: var(--space-3) 0; background: none; border: 0; color: inherit; font: inherit; text-align: left; cursor: pointer; }
        .pc-action__row:hover .pc-action__name { color: var(--accent-primary); }
        .pc-action__name { display: flex; align-items: center; gap: 6px; font-weight: 500; flex-wrap: wrap; }
        .pc-action__chev { transition: transform 0.15s; color: var(--text-tertiary); flex-shrink: 0; }
        .pc-action__chev.is-open { transform: rotate(90deg); }
        .pc-action__since { font-size: var(--text-xs); font-weight: 400; color: var(--text-tertiary); border: 1px solid var(--divider); border-radius: 999px; padding: 1px 8px; }
        .pc-action__bar { height: 8px; background: rgba(255,255,255,0.05); border-radius: 4px; overflow: hidden; }
        .pc-action__bar > span { display: block; height: 100%; background: var(--accent-primary); border-radius: 4px; }
        .pc-action__count { font-family: var(--font-mono); font-size: var(--text-sm); text-align: right; }
        .pc-action__cafes { padding: 0 0 var(--space-3) 22px; max-height: 280px; overflow-y: auto; }
        .pc-action__repeats { font-size: var(--text-xs); color: var(--text-tertiary); padding-top: var(--space-2); }

        @media (max-width: 640px) { .pc-action__row { grid-template-columns: 1fr 56px; } .pc-action__bar { display: none; } }

        @media (max-width: 1024px) { .metrics-grid { grid-template-columns: repeat(2, 1fr); } .bottom-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
